import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const SubscriptionTracker = ({ subscriptions, transactions, onManage }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Automated recurring payment detection
  const detectedRecurring = useMemo(() => {
    if (!transactions) return [];
    
    const merchantFrequency = {};
    const merchantAmounts = {};
    
    transactions?.forEach(txn => {
      if (txn?.amount < 0 && !txn?.category?.includes('income')) {
        const merchant = txn?.merchant;
        if (!merchantFrequency?.[merchant]) {
          merchantFrequency[merchant] = [];
          merchantAmounts[merchant] = [];
        }
        merchantFrequency?.[merchant]?.push(new Date(txn?.date));
        merchantAmounts?.[merchant]?.push(Math.abs(txn?.amount));
      }
    });

    const recurring = [];
    Object.keys(merchantFrequency)?.forEach(merchant => {
      const dates = merchantFrequency?.[merchant]?.sort((a, b) => a - b);
      if (dates?.length >= 2) {
        const intervals = [];
        for (let i = 1; i < dates?.length; i++) {
          const daysDiff = (dates?.[i] - dates?.[i-1]) / (1000 * 60 * 60 * 24);
          intervals?.push(daysDiff);
        }
        const avgInterval = intervals?.reduce((a, b) => a + b, 0) / intervals?.length;
        
        // Detect monthly (25-35 days) or annual (350-380 days) patterns
        if ((avgInterval >= 25 && avgInterval <= 35) || (avgInterval >= 350 && avgInterval <= 380)) {
          const avgAmount = merchantAmounts?.[merchant]?.reduce((a, b) => a + b, 0) / merchantAmounts?.[merchant]?.length;
          const isMonthly = avgInterval >= 25 && avgInterval <= 35;
          
          // Check if not already in subscriptions list
          const alreadyTracked = subscriptions?.some(sub => 
            sub?.name?.toLowerCase()?.includes(merchant?.toLowerCase()) || 
            merchant?.toLowerCase()?.includes(sub?.name?.toLowerCase())
          );
          
          if (!alreadyTracked) {
            recurring?.push({
              merchant,
              amount: avgAmount,
              frequency: isMonthly ? 'monthly' : 'annual',
              occurrences: dates?.length,
              lastCharge: dates?.[dates?.length - 1],
              confidence: Math.min(95, 60 + (dates?.length * 10))
            });
          }
        }
      }
    });
    
    return recurring?.sort((a, b) => b?.confidence - a?.confidence)?.slice(0, 5);
  }, [transactions, subscriptions]);

  // Category breakdown
  const categoryBreakdown = useMemo(() => {
    const categories = {
      'Streaming': ['Netflix', 'Spotify', 'Hulu', 'Disney', 'HBO', 'Apple Music', 'YouTube'],
      'Software': ['Adobe', 'Microsoft', 'Dropbox', 'GitHub', 'Slack', 'Zoom'],
      'Shopping': ['Amazon', 'Costco', 'Target'],
      'Fitness': ['Gym', 'Peloton', 'ClassPass', 'Fitness'],
      'Other': []
    };

    const breakdown = {
      'Streaming': 0,
      'Software': 0,
      'Shopping': 0,
      'Fitness': 0,
      'Other': 0
    };

    subscriptions?.forEach(sub => {
      let categorized = false;
      for (const [category, keywords] of Object.entries(categories)) {
        if (keywords?.some(keyword => sub?.name?.toLowerCase()?.includes(keyword?.toLowerCase()))) {
          const monthlyAmount = sub?.frequency === 'monthly' ? sub?.amount : sub?.amount / 12;
          breakdown[category] += monthlyAmount;
          categorized = true;
          break;
        }
      }
      if (!categorized) {
        const monthlyAmount = sub?.frequency === 'monthly' ? sub?.amount : sub?.amount / 12;
        breakdown['Other'] += monthlyAmount;
      }
    });

    return Object.entries(breakdown)?.filter(([_, value]) => value > 0)?.map(([name, value]) => ({ name, value }));
  }, [subscriptions]);

  const totalMonthly = subscriptions?.reduce((sum, sub) => {
    if (sub?.frequency === 'monthly') return sum + sub?.amount;
    if (sub?.frequency === 'annual') return sum + (sub?.amount / 12);
    return sum;
  }, 0);

  const totalAnnual = subscriptions?.reduce((sum, sub) => {
    if (sub?.frequency === 'monthly') return sum + (sub?.amount * 12);
    if (sub?.frequency === 'annual') return sum + sub?.amount;
    return sum;
  }, 0);

  const avgPerSubscription = subscriptions?.length > 0 ? totalMonthly / subscriptions?.length : 0;

  const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'trial': return 'text-warning';
      case 'expiring': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'trial': return 'Clock';
      case 'expiring': return 'AlertCircle';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-2">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            Subscription Tracker
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            {subscriptions?.length} active subscriptions • {detectedRecurring?.length} detected recurring
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => onManage('add')}
        >
          Add
        </Button>
      </div>
      {/* Cost Breakdown Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="p-3 md:p-4 bg-primary/10 border border-primary/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Monthly Total
            </span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground">
            {formatCurrency(totalMonthly)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-muted-foreground" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Annual Total
            </span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground">
            {formatCurrency(totalAnnual)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="BarChart3" size={16} className="text-muted-foreground" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Avg per Sub
            </span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground">
            {formatCurrency(avgPerSubscription)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-warning/10 border border-warning/20 rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="AlertCircle" size={16} className="text-warning" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Detected New
            </span>
          </div>
          <p className="text-xl md:text-2xl font-bold text-foreground">
            {detectedRecurring?.length}
          </p>
        </div>
      </div>
      {/* Category Breakdown Chart */}
      {categoryBreakdown?.length > 0 && (
        <div className="mb-4 md:mb-6 p-4 bg-muted rounded-xl">
          <h4 className="text-sm md:text-base font-semibold text-foreground mb-4">
            Cost Breakdown by Category
          </h4>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryBreakdown?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-card)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                  formatter={(value) => formatCurrency(value)}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {/* Detected Recurring Payments */}
      {detectedRecurring?.length > 0 && (
        <div className="mb-4 md:mb-6 p-4 bg-warning/10 border border-warning/20 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Sparkles" size={20} className="text-warning" />
            <h4 className="text-sm md:text-base font-semibold text-foreground">
              Detected Recurring Payments
            </h4>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground mb-3">
            Our AI detected these recurring charges from your transaction history. Add them to track better.
          </p>
          <div className="space-y-2">
            {detectedRecurring?.map((payment, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-card rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold text-foreground">{payment?.merchant}</p>
                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">
                      {payment?.confidence}% confidence
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(payment?.amount)}/{payment?.frequency === 'monthly' ? 'mo' : 'yr'} • 
                    {payment?.occurrences} occurrences • Last: {formatDate(payment?.lastCharge)}
                  </p>
                </div>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  onClick={() => onManage('add', payment)}
                >
                  Track
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Active Subscriptions List */}
      <div className="space-y-3">
        <h4 className="text-sm md:text-base font-semibold text-foreground">
          Active Subscriptions
        </h4>
        {subscriptions?.map((subscription) => (
          <div
            key={subscription?.id}
            className="flex items-center gap-3 md:gap-4 p-3 md:p-4 bg-muted rounded-xl hover:bg-muted/80 transition-smooth"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-background flex items-center justify-center overflow-hidden flex-shrink-0">
              <Image 
                src={subscription?.logo} 
                alt={subscription?.logoAlt}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <h4 className="text-sm md:text-base font-semibold text-foreground truncate">
                  {subscription?.name}
                </h4>
                <div className={`flex items-center gap-1 ${getStatusColor(subscription?.status)} flex-shrink-0`}>
                  <Icon name={getStatusIcon(subscription?.status)} size={14} />
                  <span className="text-xs font-medium capitalize hidden sm:inline">
                    {subscription?.status}
                  </span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs md:text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">
                  {formatCurrency(subscription?.amount)}/{subscription?.frequency === 'monthly' ? 'mo' : 'yr'}
                </span>
                <span className="hidden sm:inline">•</span>
                <span>Next billing: {formatDate(subscription?.nextBilling)}</span>
              </div>

              {subscription?.trialEnds && (
                <div className="mt-2 flex items-center gap-2 text-xs text-warning">
                  <Icon name="AlertCircle" size={12} />
                  <span>Trial ends {formatDate(subscription?.trialEnds)}</span>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              iconName="Settings"
              onClick={() => onManage(subscription?.id)}
              className="flex-shrink-0"
            >
              Manage
            </Button>
          </div>
        ))}
      </div>
      {subscriptions?.some(sub => sub?.status === 'expiring' || sub?.trialEnds) && (
        <div className="mt-4 md:mt-6 p-3 md:p-4 bg-warning/10 border border-warning/20 rounded-xl">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                Action Required
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                You have subscriptions expiring soon or trials ending. Review and manage them to avoid unexpected charges.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionTracker;